import React, { useEffect, useState } from 'react';
import { Box, Button, FormLabel, Grid, Heading, Stack } from '@chakra-ui/react';
import { OverviewCard } from 'components/OverviewCard/OverviewCard';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import {
  DailyAllowanceByIdResults,
  useDailyAllowanceFormMutation,
  useDeleteExpenseLine,
  useGetPerDiemOptions,
  useUpdateDailyAllowanceById,
} from 'api/services/TravelExpenses';
import { useLocation, useNavigate } from 'react-router-dom';
import { TravelRequestResult } from 'api/types/types';
import { FormInputs } from '../types/formInputsDailyAllowance';
import { defaultValues } from '../data/formDefaultValuesDailyAllowance';
import { DailyAllowanceFormValidation } from '../validation/validationDailyAllowance';
import { LoadingContainer } from 'components/LoadingContainer/LoadingContainer';
import { DailyAllowanceTableOverview } from 'routes/travelExpenses/DailyAllowance/DailyAllowanceTableOverview';
import { PageHeader } from 'routes/layouts/pageHeader';

type DailyAllowanceFormProps = {
  travelRequestData: TravelRequestResult | undefined;
  fetchDailyAllowanceExpense: () => void;
  dailyAllowanceExpenseData: DailyAllowanceByIdResults[];
  dailyAllowanceExpenseError: boolean;
  isFetchingDailyAllowanceExpenses: boolean;
};

export const DailyAllowanceForm = ({
  travelRequestData,
  fetchDailyAllowanceExpense,
  dailyAllowanceExpenseData,
  dailyAllowanceExpenseError,
  isFetchingDailyAllowanceExpenses,
}: DailyAllowanceFormProps): JSX.Element => {
  const { data: perDiemOptions } = useGetPerDiemOptions();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');
  const itemId = queryParams.get('itemId');
  const action = queryParams.get('action');

  const adminPath = location?.pathname?.startsWith('/admin/') ? '/admin' : '';

  const [lineId, setLineId] = useState<string | undefined>('');

  const [countryOption, setCountryOption] = useState<
    {
      label: string;
      value: string;
      description: string;
      categoryAndQty: { half: string; full: string };
      color: string;
    }[]
  >([]);

  const {
    mutateAsync: updateDailyAllowance,
    isSuccess: updatedDailyAllowance,
  } = useUpdateDailyAllowanceById(id, itemId);

  const { handleSubmit, watch, reset, setValue, trigger } = useForm<FormInputs>(
    {
      // @ts-ignore
      resolver: yupResolver(DailyAllowanceFormValidation),
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: {
        ...defaultValues,
      },
    }
  );

  const {
    mutateAsync: createExpenseForRequest,
    isLoading,
    isSuccess: createdExpenseForRequest,
  } = useDailyAllowanceFormMutation(id);

  const { mutateAsync: deleteCostLine, isLoading: isDeletingCostLine } =
    useDeleteExpenseLine(id, lineId);

  const handleDeleteCostLine = async (
    lId: string | undefined
  ): Promise<void> => {
    try {
      await new Promise<void>((resolve) => {
        setLineId(lId);
        resolve();
      });
      await deleteCostLine(undefined);
      fetchDailyAllowanceExpense();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (perDiemOptions) {
      const colorMap = {
        0: 'colorGreenlight',
        1: 'green',
        2: 'yellow',
        3: 'colorConcrete',
      };
      const newArray = perDiemOptions.map((item, idx) => ({
        label: item.category,
        value: item.category,
        description: item.description,
        categoryAndQty: item.categoryAndQty,
        color: colorMap[idx],
      }));
      setCountryOption(newArray);
    }
  }, [perDiemOptions]);

  useEffect(() => {
    if (createdExpenseForRequest || updatedDailyAllowance) {
      if (travelRequestData)
        setValue('costDateTo', travelRequestData.arrivalDate);
      fetchDailyAllowanceExpense();
    }
    // eslint-disable-next-line
  }, [createdExpenseForRequest, updatedDailyAllowance]);

  useEffect(() => {
    if (
      itemId &&
      dailyAllowanceExpenseData.length > 0 &&
      action === 'dagpeningar'
    ) {
      const selectedItem = dailyAllowanceExpenseData.filter(
        (el) => el.id === itemId
      );
      Object.keys(selectedItem[0]).forEach((fieldName) => {
        if (fieldName === 'categoryName') {
          const catReg = selectedItem[0][fieldName].match(/^\S+\s\S+/);
          if (catReg) setValue('categoryName', catReg[0]);
        } else {
          setValue(fieldName as any, selectedItem[0][fieldName]);
        }
      });
    }
  }, [itemId, setValue, action, dailyAllowanceExpenseData]);

  const checkIfCityExists = (city: string): string => {
    for (const option of countryOption) {
      if (option.description.includes(city)) {
        return option.label;
      }
    }
    return 'Flokkur 4';
  };

  useEffect(() => {
    if (travelRequestData && countryOption) {
      setValue('categoryName', checkIfCityExists(travelRequestData.city));
      setValue('city', travelRequestData.city);
      setValue('country', travelRequestData?.country);
      setValue('costDateTo', travelRequestData.arrivalDate);
      setValue('costDateFrom', travelRequestData.departureDate);
    }
    // eslint-disable-next-line
  }, [travelRequestData, countryOption]);

  const onSubmit: SubmitHandler<FormInputs> = async (
    formData: FormInputs,
    event: React.BaseSyntheticEvent<object, any, any> | undefined
  ) => {
    event?.preventDefault();
    const isValid = await trigger();
    if (isValid) {
      const { ...submitData } = formData;
      const categoryDescription = countryOption.find(
        (el) => el.label === formData.categoryName
      )?.categoryAndQty;

      const newCategoryName =
        travelRequestData?.perDiemOption === 'adrid-dagpeningar'
          ? categoryDescription?.half
          : categoryDescription?.full;

      const dataToSubmit = {
        ...submitData,
        expenseReportNumber: travelRequestData?.erpId,
        employeePersonnelNumber: travelRequestData?.employeeId,
        categoryName: newCategoryName as string,
        costDateFrom: moment(formData.costDateFrom).format('yyyy-MM-DD'),
        costDateTo: moment(formData.costDateTo)
          .subtract(travelRequestData?.addExtraDays, 'days')
          .format('yyyy-MM-DD'),
        costType: '4215',
      };
      try {
        if (itemId) {
          await updateDailyAllowance(dataToSubmit);
        } else {
          await createExpenseForRequest(dataToSubmit);
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const selectedCategory = watch('categoryName');

  return (
    <LoadingContainer isLoaded={countryOption.length > 0}>
      <Grid
        templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(1, 1fr)' }}
        gap={5}
        maxW="container.lg"
      >
        <PageHeader
          left={
            <Box alignItems="center" display="flex">
              <Heading as="h2" size="md" mr={2}>
                Ferðast til: <strong>{travelRequestData?.city}</strong>,{' '}
                <strong>{travelRequestData?.country}</strong>
              </Heading>
            </Box>
          }
        />
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <FormLabel>
              Veldu þann flokk sem að ferðaborgin tilheyrir og staðfesta
            </FormLabel>
            <Box
              as="div"
              display="flex"
              flexDir="row"
              justifyContent="space-between"
              mb={10}
            >
              {countryOption.map((value) => (
                <Box as="div" key={value.description}>
                  <OverviewCard
                    title={value?.label}
                    value={value?.description}
                    isSelected={selectedCategory === value?.label}
                    color={value?.color}
                    onClick={(): void => setValue('categoryName', value?.label)}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* <Grid
            templateColumns={{
              base: 'repeat(1, 1fr)',
              md: 'repeat(1, 1fr)',
            }}
            gap={10}
          >
            <SimpleGrid spacing={5} columns={{ base: 1, md: 2 }}>
              <Box>
                <FormControl isInvalid={!!errors.country}>
                  <FormLabel>Land</FormLabel>
                  <Input placeholder="Land" {...register('country')} disabled />
                  <FormErrorMessage>{errors.country?.message}</FormErrorMessage>
                </FormControl>
              </Box>
              <Box>
                <FormControl isInvalid={!!errors.city}>
                  <FormLabel>Borg</FormLabel>
                  <Input placeholder="Borg" {...register('city')} disabled />
                  <FormErrorMessage>{errors.city?.message}</FormErrorMessage>
                </FormControl>
              </Box>
            </SimpleGrid>
          </Grid> */}
          {/* <SimpleGrid spacing={5} columns={{ base: 1, md: 2 }}>
            <Box>
              <FormControl isInvalid={!!errors.costDateFrom}>
                <FormLabel>Dagsetning frá</FormLabel>
                <Controller
                  control={control}
                  name="costDateFrom"
                  render={({ field: { name } }): JSX.Element => (
                    <DatePicker
                      name={name}
                      date={costDateFrom ? moment.utc(costDateFrom) : null}
                      id="datepicker"
                      placeholder="Veldu dagsetningu"
                      onChange={(value): void => {
                        setValue('costDateFrom', moment(value).format());
                      }}
                      startDate={moment(startDate)}
                      endDate={moment(endDate)}
                      onlySpecificRange
                      error={!!errors.costDateFrom}
                    />
                  )}
                />
                <FormErrorMessage>
                  {errors.costDateFrom?.message}
                </FormErrorMessage>
              </FormControl>
            </Box>
            <FormControl isInvalid={!!errors.costDateTo}>
              <FormLabel>Dagsetning til</FormLabel>
              <Controller
                control={control}
                name="costDateTo"
                render={({ field: { name } }): JSX.Element => (
                  <DatePicker
                    name={name}
                    date={costDateTo ? moment.utc(costDateTo) : null}
                    id="datepicker"
                    placeholder="Veldu dagsetningu"
                    onChange={(value): void => {
                      setValue('costDateTo', moment(value).format());
                    }}
                    onlySpecificRange
                    startDate={moment(startDate)}
                    endDate={moment(endDate)}
                    error={!!errors.costDateTo}
                  />
                )}
              />
              <FormErrorMessage>{errors.costDateTo?.message}</FormErrorMessage>
            </FormControl>
          </SimpleGrid> */}
          {/* <SimpleGrid spacing={5} mt={5} columns={{ base: 1, md: 2 }}>
            <FormControl isInvalid={!!errors.categoryName}>
              <FormLabel>Flokkur</FormLabel>
              <Controller
                control={control}
                name="categoryName"
                render={({ field: { onChange } }): JSX.Element => (
                  <Select
                    onChange={onChange}
                    options={countryOption}
                    placeholder="Leita eftir kostnaðartegund"
                    defaultValue={
                      selectedCategory
                        ? {
                            label: selectedCategory,
                            value: selectedCategory,
                          }
                        : null
                    }
                    error={!!errors.categoryName}
                  />
                )}
              />
            </FormControl>
          </SimpleGrid> */}
          <Stack direction="row" mt={5}>
            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              loadingText="Augnablik..."
              name="submit-form"
              isDisabled={dailyAllowanceExpenseData.length > 0}
            >
              Staðfesta
            </Button>
            {itemId && (
              <Button
                variant="secondary"
                onClick={(): void => {
                  reset();
                  navigate(
                    `${adminPath}/ferdabeidnir/ferdabeidni/preview?id=${id}&action=dagpeningar`,
                    { state: { hasActions: true } }
                  );
                }}
              >
                Skrá nýja dagpeninga
              </Button>
            )}
          </Stack>
        </Box>
        <DailyAllowanceTableOverview
          data={dailyAllowanceExpenseData}
          isError={dailyAllowanceExpenseError}
          isFetching={isFetchingDailyAllowanceExpenses}
          noDataText="Engir dagpeningar skráðir ennþá"
          handleDeleteCostLine={handleDeleteCostLine}
          isDeletingCostLine={isDeletingCostLine}
        />
      </Grid>
    </LoadingContainer>
  );
};
